import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Pin, Info } from 'lucide-react';
import mapi from './api';
import { MobileShell, MobileHeader, C } from './MobileShell';
import { formatDate } from '@/lib/utils';

const STATUS_BADGE = {
  pending: { label: 'Wird geprüft', color: '#f59e0b' },
  rejected: { label: 'Abgelehnt', color: '#ef4444' },
};

export default function MobilePinboard() {
  const [posts, setPosts] = useState([]);
  const [tab, setTab] = useState('all');
  const [form, setForm] = useState({ title: '', body: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await mapi.get('/app/pinboard');
      setPosts(r.data || []);
    } catch (e) { toast.error('Pinnwand konnte nicht geladen werden'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Titel fehlt'); return; }
    setBusy(true);
    try {
      await mapi.post('/app/pinboard', form);
      toast.success('Beitrag eingereicht — er erscheint nach Prüfung durch die Verwaltung');
      setForm({ title: '', body: '' });
      setTab('mine');
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Einreichen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const visible = tab === 'mine' ? posts.filter((p) => p.is_mine)
    : posts.filter((p) => p.status === 'online');

  const fmt = (d) => formatDate(d);
  const inputStyle = { background: C.card, border: `1px solid ${C.border}`, color: C.text };

  return (
    <MobileShell>
      <MobileHeader title="Pinnwand" onRefresh={load} backTo="/m" />
      <div className="flex-1 overflow-y-auto px-4 py-3 pb-24">
        {tab === 'new' ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 text-xs rounded-lg px-3 py-2.5"
                 style={{ background: '#23262e', color: C.dim }}>
              <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: C.red }} />
              Dein Beitrag wird erst nach Freigabe durch die Verwaltung für alle sichtbar.
            </div>
            <input className="w-full rounded-lg px-4 py-3 text-sm outline-none" style={inputStyle}
                   placeholder="Titel (z. B. „Suche Klavier-Noten…“)" value={form.title}
                   onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="mobile-pinboard-title" />
            <textarea className="w-full rounded-lg px-4 py-3 text-sm outline-none" style={inputStyle} rows={6}
                      placeholder="Dein Text…" value={form.body}
                      onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} data-testid="mobile-pinboard-body" />
            <button onClick={submit} disabled={busy} data-testid="mobile-pinboard-submit"
                    className="w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50" style={{ background: C.red }}>
              Beitrag einreichen
            </button>
          </div>
        ) : (
          <>
            {!visible.length && (
              <p className="text-center text-sm mt-10" style={{ color: C.dim }}>
                {tab === 'mine' ? 'Du hast noch keine Beiträge' : 'Noch keine Beiträge auf der Pinnwand'}
              </p>
            )}
            {visible.map((p) => (
              <div key={p.id} className="rounded-xl mb-3 px-4 py-3.5"
                   style={{ background: C.card, border: `1px solid ${C.border}` }}
                   data-testid={`mobile-pinboard-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold" style={{ color: C.text }}>
                    <Pin className="w-3.5 h-3.5 inline mr-1.5" style={{ color: C.red }} />{p.title}
                  </p>
                  {p.is_mine && STATUS_BADGE[p.status] && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white shrink-0"
                          style={{ background: STATUS_BADGE[p.status].color }}
                          data-testid={`mobile-pinboard-status-${p.id}`}>
                      {STATUS_BADGE[p.status].label}
                    </span>
                  )}
                </div>
                <p className="text-[11px] mt-0.5" style={{ color: C.dim }}>{p.author_name} · {fmt(p.created_at)}</p>
                {p.body && <p className="text-sm mt-2 whitespace-pre-wrap" style={{ color: '#c9c9d1' }}>{p.body}</p>}
              </div>
            ))}
          </>
        )}
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md flex"
           style={{ background: C.card, borderTop: `1px solid ${C.border}` }}>
        {[{ k: 'all', label: 'Alle' }, { k: 'mine', label: 'Meine' }, { k: 'new', label: 'Neu' }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`mobile-pinboard-tab-${t.k}`}
                  className="flex-1 py-3 text-xs font-semibold" style={{ color: tab === t.k ? C.red : C.dim }}>
            {t.label}
          </button>
        ))}
      </div>
    </MobileShell>
  );
}
